/* Swamp Cracker redesign: shared carousel, hero strip and quick-add behaviour. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Scroll-snap rail with external prev/next buttons.
     Markup: <sc-carousel> containing one or more [data-sc-rail] and optional
     [data-sc-prev] / [data-sc-next] / [data-sc-arrows]. With [role="tab"]
     buttons, each tab shows its [role="tabpanel"] and the arrows drive the
     rail inside the visible panel. */
  class ScCarousel extends HTMLElement {
    connectedCallback() {
      this.rails = Array.from(this.querySelectorAll('[data-sc-rail]'));
      if (!this.rails.length) return;
      this.prev = this.querySelectorAll('[data-sc-prev]');
      this.next = this.querySelectorAll('[data-sc-next]');
      this.arrows = this.querySelectorAll('[data-sc-arrows]');
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.indicator = this.querySelector('[data-tab-indicator]');

      this.onPrev = () => this.step(-1);
      this.onNext = () => this.step(1);
      this.update = this.update.bind(this);
      this.onResize = () => {
        this.moveIndicator();
        this.update();
      };
      this.onTabClick = (event) => this.selectTab(event.currentTarget);
      this.onTabKey = this.onTabKey.bind(this);

      this.prev.forEach((btn) => btn.addEventListener('click', this.onPrev));
      this.next.forEach((btn) => btn.addEventListener('click', this.onNext));
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.onTabClick);
        tab.addEventListener('keydown', this.onTabKey);
      });
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.rails.forEach((rail) => {
        rail.addEventListener('scroll', this.update, { passive: true });
        this.resizeObserver.observe(rail);
      });
      if (this.indicator) this.resizeObserver.observe(this.indicator.parentElement);
      this.moveIndicator();
      this.update();
    }

    disconnectedCallback() {
      if (!this.rails || !this.rails.length) return;
      this.prev.forEach((btn) => btn.removeEventListener('click', this.onPrev));
      this.next.forEach((btn) => btn.removeEventListener('click', this.onNext));
      this.tabs.forEach((tab) => {
        tab.removeEventListener('click', this.onTabClick);
        tab.removeEventListener('keydown', this.onTabKey);
      });
      this.rails.forEach((rail) => rail.removeEventListener('scroll', this.update));
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    get rail() {
      return this.rails.find((rail) => !rail.closest('[hidden]')) || this.rails[0];
    }

    selectTab(tab) {
      if (tab.getAttribute('aria-selected') === 'true') return;
      this.tabs.forEach((other) => {
        var selected = other === tab;
        other.setAttribute('aria-selected', String(selected));
        other.tabIndex = selected ? 0 : -1;
        var panel = document.getElementById(other.getAttribute('aria-controls'));
        if (!panel) return;
        panel.hidden = !selected;
        if (selected) this.enterPanel(panel);
      });
      this.moveIndicator();
      this.update();
    }

    /* Sliding underline under the selected tab. */
    moveIndicator() {
      if (!this.indicator) return;
      var tab = this.tabs.find((t) => t.getAttribute('aria-selected') === 'true');
      if (!tab) return;
      this.indicator.style.width = tab.offsetWidth + 'px';
      this.indicator.style.transform = 'translateX(' + tab.offsetLeft + 'px)';
      var list = this.indicator.parentElement;
      if (!list.classList.contains('has-indicator')) {
        list.classList.add('has-indicator');
        // Animate only later moves, not the initial placement.
        requestAnimationFrame(() => requestAnimationFrame(() => list.classList.add('is-ready')));
      }
    }

    /* Restart the rail at its first card and let the cards animate in. */
    enterPanel(panel) {
      var rail = panel.querySelector('[data-sc-rail]');
      if (rail) rail.scrollLeft = 0;
      panel.classList.remove('is-entering');
      void panel.offsetWidth;
      panel.classList.add('is-entering');
      window.clearTimeout(panel.scEnterTimer);
      panel.scEnterTimer = window.setTimeout(() => panel.classList.remove('is-entering'), 1200);
    }

    onTabKey(event) {
      var index = this.tabs.indexOf(event.currentTarget);
      var target = null;
      if (event.key === 'ArrowRight') target = this.tabs[(index + 1) % this.tabs.length];
      else if (event.key === 'ArrowLeft') target = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
      else if (event.key === 'Home') target = this.tabs[0];
      else if (event.key === 'End') target = this.tabs[this.tabs.length - 1];
      if (!target) return;
      event.preventDefault();
      target.focus();
      this.selectTab(target);
    }

    stepSize() {
      var item = this.rail.querySelector(':scope > *:not([hidden])');
      if (!item) return this.rail.clientWidth;
      var gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
      return item.getBoundingClientRect().width + gap;
    }

    /* Scroll to an absolute target clamped to the rail's ends. An unclamped
       smooth scrollBy past either end overshoots on iOS Safari and can leave
       the snap rail stuck in its overscroll, showing blank space. */
    step(direction) {
      var rail = this.rail;
      var dir = getComputedStyle(rail).direction === 'rtl' ? -1 : 1;
      var max = rail.scrollWidth - rail.clientWidth;
      var pos = Math.abs(rail.scrollLeft);
      var target = Math.min(max, Math.max(0, Math.round(pos + this.stepSize() * direction)));
      if (Math.abs(target - pos) < 1) return;
      rail.scrollTo({
        left: target * dir,
        behavior: reduceMotion.matches ? 'auto' : 'smooth'
      });
    }

    update() {
      var max = this.rail.scrollWidth - this.rail.clientWidth;
      var pos = Math.abs(this.rail.scrollLeft);
      var scrollable = max > 1;
      this.arrows.forEach((el) => (el.hidden = !scrollable));
      this.prev.forEach((btn) => (btn.disabled = !scrollable || pos <= 1));
      this.next.forEach((btn) => (btn.disabled = !scrollable || pos >= max - 1));
    }
  }

  /* Hero slideshow (Figma annotation: "move slider with different images every
     4 sec"). The hero image cross-fades to the next slide every 4s on desktop;
     the strip shows the upcoming slides, rotating one step per change, and a
     thumbnail jumps to its slide. Pause/play toggle; hover/focus pauses; starts
     paused for reduced motion; static where the strip is hidden (mobile). */
  class ScHeroStrip extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-strip-track]');
      this.toggle = this.querySelector('[data-strip-toggle]');
      var root = this.closest('.sc-hero__inner');
      this.slides = root ? Array.from(root.querySelectorAll('[data-hero-slide]')) : [];
      if (!this.track || this.slides.length < 2) {
        if (this.toggle) this.toggle.hidden = true;
        return;
      }
      this.interval = parseInt(this.dataset.interval, 10) || 4000;
      this.userPaused = reduceMotion.matches;
      this.hoverPaused = false;
      this.active = 0;
      this.desktop = window.matchMedia('(min-width: 769px)');

      this.onToggle = () => {
        this.userPaused = !this.userPaused;
        this.sync();
      };
      this.onEnter = () => {
        this.hoverPaused = true;
        this.sync();
      };
      this.onLeave = (event) => {
        if (event.type === 'focusout' && this.contains(event.relatedTarget)) return;
        this.hoverPaused = false;
        this.sync();
      };
      this.onThumb = (event) => {
        var thumb = event.target.closest('[data-strip-thumb]');
        if (!thumb) return;
        this.show(this.slides.findIndex((slide) => slide.dataset.index === thumb.dataset.index), false);
        this.restart();
      };
      this.onChange = () => this.sync();

      this.toggle.addEventListener('click', this.onToggle);
      this.track.addEventListener('click', this.onThumb);
      this.addEventListener('mouseenter', this.onEnter);
      this.addEventListener('mouseleave', this.onLeave);
      this.addEventListener('focusin', this.onEnter);
      this.addEventListener('focusout', this.onLeave);
      document.addEventListener('visibilitychange', this.onChange);
      this.desktop.addEventListener('change', this.onChange);
      this.sync();
    }

    disconnectedCallback() {
      this.stop();
      if (!this.onToggle) return;
      this.toggle.removeEventListener('click', this.onToggle);
      this.track.removeEventListener('click', this.onThumb);
      this.removeEventListener('mouseenter', this.onEnter);
      this.removeEventListener('mouseleave', this.onLeave);
      this.removeEventListener('focusin', this.onEnter);
      this.removeEventListener('focusout', this.onLeave);
      document.removeEventListener('visibilitychange', this.onChange);
      this.desktop.removeEventListener('change', this.onChange);
    }

    sync() {
      var running = !this.userPaused && !this.hoverPaused && !document.hidden && this.desktop.matches;
      this.toggle.setAttribute('aria-pressed', String(this.userPaused));
      this.toggle.setAttribute('aria-label', this.userPaused ? this.dataset.labelPlay : this.dataset.labelPause);
      this.classList.toggle('is-paused', this.userPaused);
      if (running) this.start();
      else this.stop();
    }

    start() {
      if (this.timer) return;
      this.timer = window.setInterval(() => this.show(this.active + 1, true), this.interval);
    }

    stop() {
      window.clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.stop();
      this.sync();
    }

    show(index, animate) {
      var count = this.slides.length;
      if (index < 0) return;
      this.active = ((index % count) + count) % count;
      this.slides.forEach((slide, i) => {
        var current = i === this.active;
        slide.classList.toggle('is-active', current);
        slide.setAttribute('aria-hidden', String(!current));
      });
      this.queueFrom(this.slides[(this.active + 1) % count].dataset.index, animate);
    }

    /* Rotate the strip so its first thumbnail is the given slide. */
    queueFrom(slideIndex, animate) {
      var track = this.track;
      var first = track.firstElementChild;
      var target = track.querySelector('[data-strip-thumb][data-index="' + slideIndex + '"]');
      if (!target) return;
      var targetItem = target.closest('li');
      if (first === targetItem) return;
      var reorder = () => {
        track.style.transition = 'none';
        track.style.transform = '';
        while (track.firstElementChild !== targetItem) track.appendChild(track.firstElementChild);
      };
      if (!animate || reduceMotion.matches || first.nextElementSibling !== targetItem) {
        reorder();
        return;
      }
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      track.style.transition = 'transform 0.5s ease';
      track.style.transform = 'translateX(' + -(first.getBoundingClientRect().width + gap) + 'px)';
      track.addEventListener('transitionend', reorder, { once: true });
    }
  }

  /* Quick add (cart-plus buttons). Single-variant products post to /cart/add.js
     and announce ajaxProduct:added like the theme's product form, so the cart
     drawer rebuilds and opens. Multi-variant products open the theme's quick view
     so the shopper chooses an option first, on every screen size. */
  // One quick view per product, shared by every button for it: the same product
  // can appear on several cards (e.g. two reviews of one hat), and each card
  // renders its own modal with the same IDs.
  var quickViews = {};

  function openQuickView(form) {
    var handle = form.dataset.productHandle;
    var productId = form.dataset.productId;
    var view = quickViews[productId];

    if (view) {
      if (view.modal) view.modal.open();
      return;
    }

    // Use the modal rendered next to this button (sc-quick-add), not the first
    // element with its ID, which may belong to another card.
    var modalEl = form.nextElementSibling;
    if (!modalEl || !modalEl.matches('.modal--quick-shop')) modalEl = document.getElementById('QuickShopModal-' + productId);
    var holder = modalEl && modalEl.querySelector('[id="QuickShopHolder-' + handle + '"]');

    if (!holder || !window.theme || !theme.Modals) {
      window.location.href = form.dataset.productUrl;
      return;
    }

    // The modal is rendered next to the button, which can sit inside a
    // transformed or hidden popover; fixed positioning needs it at body level.
    if (modalEl.parentElement !== document.body) document.body.appendChild(modalEl);

    // theme.Modals finds its element by ID; a duplicate earlier in the page
    // (checked after the move above) would make it open that empty copy.
    var modalId = modalEl.id;
    if (document.getElementById(modalId) !== modalEl) {
      modalId = modalId + '-sc';
      modalEl.id = modalId;
    }

    view = quickViews[productId] = { modal: null };

    // product.sc-modal.json renders the redesigned panel. The stock quick shop
    // on collection pages still uses ?view=modal, so that path is untouched.
    var url = (theme.routes.home + '/products/' + handle + '?view=sc-modal').replace('//', '/');
    form.setAttribute('aria-busy', 'true');
    fetch(url)
      .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
      .then((text) => {
        var html = document.createElement('div');
        html.innerHTML = text;
        var div = html.querySelector('.sc-pdp[data-product-handle="' + handle + '"]');
        if (!div) throw new Error('Quick view markup missing');
        holder.innerHTML = '';
        holder.append(div);
        view.modal = new theme.Modals(modalId, 'sc-quick-' + productId);
        // sc-product-form and sc-product-gallery are custom elements: they
        // upgrade themselves once appended, so there is no section to register.
        view.modal.open();
      })
      .catch(() => {
        delete quickViews[productId];
        window.location.href = form.dataset.productUrl;
      })
      .finally(() => form.removeAttribute('aria-busy'));
  }

  function addToCart(form) {
    var button = form.querySelector('[data-sc-quick-add-btn]');
    var status = form.querySelector('[data-sc-status]');
    if (button) button.disabled = true;
    form.setAttribute('aria-busy', 'true');

    fetch((window.theme && theme.routes.cartAdd) || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: Number(form.dataset.variantId), quantity: 1 }] })
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data: data })))
      .then((result) => {
        if (!result.ok) throw result.data;
        if (status) status.textContent = form.dataset.addedText || '';
        var product = result.data.items ? result.data.items[0] : result.data;
        // Same event the theme's product form emits; the cart drawer rebuilds and opens.
        // The hidden id input lets listeners that read the form's variant find it.
        var idInput = document.createElement('input');
        idInput.type = 'hidden';
        idInput.name = 'id';
        idInput.value = form.dataset.variantId;
        form.appendChild(idInput);
        form.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: product, addToCartBtn: button }, bubbles: true }));
        idInput.remove();
      })
      .catch((error) => {
        if (status) status.textContent = (error && (error.description || error.message)) || form.dataset.errorText || '';
      })
      .finally(() => {
        if (button) button.disabled = false;
        form.removeAttribute('aria-busy');
      });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-sc-quick-add-btn]');
    if (!button || button.disabled) return;
    var form = button.closest('[data-sc-quick-add]');
    if (!form) return;
    event.preventDefault();
    if (form.dataset.mode === 'quick-view') openQuickView(form);
    else addToCart(form);
  });

  /* Hotspot / product popover toggles: one open per container. */
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-sc-pop-trigger]');
    var openPanels = document.querySelectorAll('[data-sc-pop].is-open');
    openPanels.forEach(function (panel) {
      if (trigger && panel.id === trigger.getAttribute('aria-controls')) return;
      if (panel.contains(event.target)) return;
      panel.classList.remove('is-open');
      var owner = document.querySelector('[aria-controls="' + panel.id + '"]');
      if (owner) owner.setAttribute('aria-expanded', 'false');
    });
    if (!trigger) return;
    var panel = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!panel) return;
    var open = !panel.classList.contains('is-open');
    panel.classList.toggle('is-open', open);
    trigger.setAttribute('aria-expanded', String(open));
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-sc-pop].is-open').forEach(function (panel) {
      panel.classList.remove('is-open');
      var owner = document.querySelector('[aria-controls="' + panel.id + '"]');
      if (owner) {
        owner.setAttribute('aria-expanded', 'false');
        owner.focus();
      }
    });
  });

  /* Sliding accordions: <details data-sc-slide> animates its body's height
     instead of snapping open. The native open state still owns visibility, so
     find-in-page, no-JS and reduced motion keep the plain toggle. */
  var slideAnimations = new WeakMap();
  document.addEventListener('click', function (event) {
    var summary = event.target.closest('summary');
    var details = summary && summary.parentElement;
    if (!details || !details.matches('details[data-sc-slide]') || reduceMotion.matches) return;
    var body = details.querySelector(':scope > :not(summary)');
    if (!body || typeof body.animate !== 'function') return;
    event.preventDefault();

    var running = slideAnimations.get(details);
    var state = details.dataset.scSlide;
    var closing = state === 'opening' || (state !== 'closing' && details.open);
    // A closed <details> may still report its body's full height, so a rest-state open starts from 0.
    var from = running || closing ? body.getBoundingClientRect().height : 0;
    if (running) running.cancel();
    if (!closing) details.open = true;
    details.dataset.scSlide = closing ? 'closing' : 'opening';
    var to = closing ? 0 : body.scrollHeight;

    body.style.overflow = 'hidden';
    var animation = body.animate([{ height: from + 'px' }, { height: to + 'px' }], { duration: 300, easing: 'ease' });
    slideAnimations.set(details, animation);
    animation.onfinish = function () {
      if (closing) details.open = false;
      details.dataset.scSlide = '';
      body.style.overflow = '';
      slideAnimations.delete(details);
    };
  });


  /* Product recommendations: fetches the section from Shopify's recommendations
     endpoint (data-url) and swaps in the rendered cards. The section is
     server-rendered with the fallback collection, so a failed or empty response
     keeps that rail; the section is only hidden when there is nothing to show. */
  class ScRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.dataset.loaded) return;
      this.dataset.loaded = 'true';
      var section = this.closest('.shopify-section');
      var hideWhenEmpty = () => {
        if (section && !this.innerHTML.trim().length) section.hidden = true;
      };
      fetch(this.dataset.url)
        .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
        .then((text) => {
          var html = document.createElement('div');
          html.innerHTML = text;
          var fresh = html.querySelector('sc-recommendations');
          if (fresh && fresh.innerHTML.trim().length) {
            // The endpoint renders without the page's product, so copy that depends on it stays as first rendered.
            var kept = {};
            this.querySelectorAll('[data-sc-keep]').forEach((el) => { kept[el.dataset.scKeep] = el.innerHTML; });
            this.innerHTML = fresh.innerHTML;
            this.querySelectorAll('[data-sc-keep]').forEach((el) => {
              if (kept[el.dataset.scKeep] != null) el.innerHTML = kept[el.dataset.scKeep];
            });
          }
          hideWhenEmpty();
        })
        .catch(hideWhenEmpty);
    }
  }

  if (!customElements.get('sc-recommendations')) customElements.define('sc-recommendations', ScRecommendations);
  if (!customElements.get('sc-carousel')) customElements.define('sc-carousel', ScCarousel);
  if (!customElements.get('sc-hero-strip')) customElements.define('sc-hero-strip', ScHeroStrip);
})();
